import { api } from '../../../core/utils/apiClient';
import {
  SubscriptionPlan,
  ChargeRequest,
  ChargeResponse,
  ValidateOTPRequest,
  ValidateOTPResponse,
  SubscriptionStatus,
  TransactionsResponse,
} from '../types/billing.types';

export class BillingService {
  /**
   * Get all active subscription plans with pricing and features
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await api.get('/api/v1/billing/plans');
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to fetch plans');
    } catch (error: any) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  }

  /**
   * Initiate a subscription payment
   */
  async chargePayment(chargeData: ChargeRequest): Promise<ChargeResponse> {
    try {
      const response = await api.post('/api/v1/billing/charge', chargeData);
      if (response.ok) {
        const data = await response.json();
        // Check if the response contains an error message even with 200 status
        // Backend may return 200 with error messages for configuration issues
        const errorIndicators = ['error', 'failed', 'not configured', 'contact support', 'gateway'];
        const messageLower = (data.message || '').toLowerCase();
        const hasError = 
          data.status === 'FAILED' || 
          data.status === 'ERROR' ||
          errorIndicators.some(indicator => messageLower.includes(indicator));
        
        if (hasError) {
          throw new Error(data.message || 'Payment failed');
        }
        
        // Only return if status is one of the expected success statuses
        if (data.status !== 'OTP_REQUIRED' && data.status !== 'SUCCESS') {
          throw new Error(data.message || 'Unexpected payment status');
        }
        
        return data;
      }
      const errorData = await response.json();
      throw new Error(errorData.detail || errorData.message || 'Failed to initiate payment');
    } catch (error: any) {
      console.error('Error charging payment:', error);
      throw error;
    }
  }

  /**
   * Validate OTP to complete payment
   */
  async validateOTP(validateData: ValidateOTPRequest): Promise<ValidateOTPResponse> {
    try {
      const response = await api.post('/api/v1/billing/validate', validateData);
      if (response.ok) {
        return await response.json();
      }
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to validate OTP');
    } catch (error: any) {
      console.error('Error validating OTP:', error);
      throw error;
    }
  }

  /**
   * Get the current user's subscription status and details
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      const response = await api.get('/api/v1/billing/status');
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to fetch subscription status');
    } catch (error: any) {
      console.error('Error fetching subscription status:', error);
      throw error;
    }
  }

  /**
   * Get the user's recent payment transactions
   */
  async getTransactions(limit: number = 10): Promise<TransactionsResponse> {
    try {
      const response = await api.get(`/api/v1/billing/transactions?limit=${limit}`);
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to fetch transactions');
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }
}

export const billingService = new BillingService();
